import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import RHFController from "../Form/RHFController";
import "./formSlider.scss";

const FormSlider = ({
  name,
  label,
  step,
  min,
  max,
  size,
  valueLabelDisplay,
  options,
  defaultValues = [1, 10],
}) => {
  const [rangeValue, setRangeValue] = useState([min, max]);
  const [sliderValue, setSliderValue] = useState(defaultValues);
  const { register, control } = useFormContext();
  const currentValue = useWatch({
    control,
    name,
  });

  useEffect(() => {
    const isCurrentValueAvailable = () =>
      currentValue?.length &&
      currentValue.every((value) => !Number.isNaN(value));
    const tempSliderValue = isCurrentValueAvailable()
      ? currentValue
      : defaultValues;

    if (step === 2 && tempSliderValue[1] % 2 === 1) tempSliderValue[1]++;
    if (!isCurrentValueAvailable()) {
      tempSliderValue[1] =
        tempSliderValue[1] > rangeValue[1] ? tempSliderValue[1] : rangeValue[1];
    } else if (tempSliderValue[1] > rangeValue[1])
      tempSliderValue[1] = rangeValue[1];

    if (tempSliderValue[0] > rangeValue[1]) tempSliderValue[0] = rangeValue[0];
    setSliderValue(tempSliderValue);
  }, [rangeValue, currentValue]);

  useEffect(() => {
    setRangeValue([min, max]);
  }, [min, max]);

  const handleMouseUp = (onChange) => (event) => {
    if (step === 2) {
      const currentVal = currentValue?.length ? currentValue : rangeValue;
      let updatedValue = currentVal;
      if (currentVal[1] - currentVal[0] < 3) {
        updatedValue = [currentVal[0], currentVal[0] + 3];
      }
      if (updatedValue[0] > 17) updatedValue[0] = 17;
      if (updatedValue[1] < 4) updatedValue[1] = 4;
      onChange(updatedValue);
      setRangeValue(updatedValue);
    }
  };
  const handleChange = (onChange) => (event, newValue) => {
    const updatedValue = [newValue[0], newValue[1]];
    if (step === 2) {
      if (newValue[1] % 2 === 1) updatedValue[1] = updatedValue[1] + 1;
      if (newValue[0] % 2 === 0) updatedValue[0] = updatedValue[0] - 1;
      if (updatedValue[0] > 17) updatedValue[0] = 17;
      if (updatedValue[1] < 4) updatedValue[1] = 4;
    }

    onChange(updatedValue);
    setRangeValue(updatedValue);
  };

  const renderField = ({ onChange }) => {
    return (
      <Slider
        name={name}
        step={step}
        min={min}
        max={max}
        valueLabelDisplay={valueLabelDisplay}
        sx={{
          "& .MuiSlider-valueLabel": {
            fontSize: "0.75rem",
          },
        }}
        size={size}
        disabled={options.disabled}
        value={sliderValue}
        onMouseUp={handleMouseUp(onChange)}
        onChange={handleChange(onChange)}
      />
    );
  };

  return (
    <div className="form-slider">
      <p>{label}</p>
      <Stack spacing={2} direction="row" alignItems="center" marginTop={"40px"}>
        <p>{min}</p>
        <RHFController
          {...{
            register,
            name,
            value: currentValue,
            rules: {},
            // eslint-disable-next-line sonarjs/cognitive-complexity
            render: ({ onChange }) => (
              <Slider
                name={name}
                step={step}
                min={min}
                max={max}
                valueLabelDisplay={valueLabelDisplay}
                sx={{
                  "& .MuiSlider-valueLabel": {
                    fontSize: "0.75rem",
                  },
                }}
                size={size}
                disabled={options.disabled}
                value={sliderValue}
                onMouseUp={(event) => {
                  const currentVal = currentValue?.length
                    ? currentValue
                    : rangeValue;
                  let updatedValue = currentVal;
                  if (step === 1) {
                    if (updatedValue[1] < min + 1) updatedValue[1] = min + 1;
                    if (currentVal[1] - currentVal[0] < 1) {
                      updatedValue = [currentVal[1] - 1, currentVal[1]];
                    }
                    if (updatedValue[0] < min) updatedValue[0] = min;
                  } else if (step === 2) {
                    if (currentVal[1] - currentVal[0] < 3) {
                      updatedValue = [currentVal[0], currentVal[0] + 3];
                    }
                    if (updatedValue[0] > 17) updatedValue[0] = 17;
                    if (updatedValue[1] < 4) updatedValue[1] = 4;
                  }
                  onChange(updatedValue);
                  setRangeValue(updatedValue);
                }}
                onChange={(event, newValue) => {
                  const updatedValue = [newValue[0], newValue[1]];
                  if (step === 1) {
                    if (updatedValue[1] < min + 1) updatedValue[1] = min + 1;
                    if (updatedValue[1] - updatedValue[0] < 1) {
                      updatedValue[0] = updatedValue[0] - 1;
                    }
                    if (updatedValue[0] < min) updatedValue[0] = min;
                  } else if (step === 2) {
                    if (newValue[1] % 2 === 1)
                      updatedValue[1] = updatedValue[1] + 1;
                    if (newValue[0] % 2 === 0)
                      updatedValue[0] = updatedValue[0] - 1;
                    if (updatedValue[0] > 17) updatedValue[0] = 17;
                    if (updatedValue[1] < 4) updatedValue[1] = 4;
                  }
                  onChange(updatedValue);
                  setRangeValue(updatedValue);
                }}
              />
            ),
          }}
        />
        <p>{max}</p>
      </Stack>
    </div>
  );
};

FormSlider.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  step: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  size: PropTypes.oneOf(["small", "medium"]),
  valueLabelDisplay: PropTypes.oneOf(["on", "auto", "off"]),
  options: PropTypes.object,
};

FormSlider.defaultProps = {
  size: "medium",
  valueLabelDisplay: "auto",
};

export default FormSlider;
